import type {
  IRoutineExecutionRepository,
  ListExecutionsQuery,
  PaginatedResponse,
} from '@yawi/domain';
import { buildQueryString, DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@yawi/domain';
import type { RoutineExecution, RoutineExecutionTrigger } from '@yawi/types';
import { getApiClient, getCurrentTenant } from '../../http/apiClient';

function normalizeExecution(raw: Record<string, unknown>): RoutineExecution {
  const output = raw.output;
  const outputObject =
    typeof output === 'object' && output !== null
      ? (output as Record<string, unknown>)
      : undefined;
  const resultCount =
    typeof output === 'number'
      ? output
      : (outputObject?.resultCount as number | undefined) ??
        (raw.resultCount as number | undefined);

  const rawTrigger = raw.triggeredBy as string | undefined;
  const triggeredBy: RoutineExecutionTrigger =
    rawTrigger === 'scheduled' ? 'schedule' : rawTrigger === 'retry' ? 'retry' : 'manual';

  return {
    id: raw.id as string,
    jobId: (raw.scheduledJobId as string) ?? (raw.jobId as string),
    tenantId: (raw.tenantId as string) ?? getCurrentTenant()?.id ?? '',
    triggeredBy,
    triggeredByUser: raw.triggeredByUser as string | undefined,
    status: raw.status as RoutineExecution['status'],
    startedAt: raw.startedAt as string,
    finishedAt: raw.finishedAt as string | undefined,
    durationMs: raw.durationMs as number | undefined,
    resultCount,
    result: outputObject,
    errorMessage: (raw.error as string) ?? (raw.errorMessage as string | undefined),
    errorStack: raw.errorStack as string | undefined,
    attempt: (raw.attempt as number) ?? 1,
  };
}

export class ApiRoutineExecutionRepository implements IRoutineExecutionRepository {
  async list(query?: ListExecutionsQuery): Promise<PaginatedResponse<RoutineExecution>> {
    const client = getApiClient();
    const jobId = query?.jobId;
    const qs = buildQueryString({
      page: query?.page ?? DEFAULT_PAGE,
      limit: query?.limit ?? DEFAULT_PAGE_SIZE,
      status: query?.status,
      from: query?.from,
      to: query?.to,
      search: query?.search,
    });
    const path = jobId
      ? `v1/scheduled-jobs/${jobId}/executions?${qs}`
      : `v1/scheduled-jobs/executions?${qs}`;
    const response = await client.get<PaginatedResponse<Record<string, unknown>>>(path);
    const data = response.data.map(normalizeExecution);
    return { ...response, data };
  }

  async findById(executionId: string): Promise<RoutineExecution | null> {
    const client = getApiClient();
    const raw = await client.get<Record<string, unknown>>(
      `v1/scheduled-jobs/executions/${executionId}`,
    );
    return normalizeExecution(raw);
  }
}